import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Video, Clock, CheckSquare, Youtube, Music, Users, ArrowRight, Sparkles, Heart, Play, HelpCircle, GraduationCap, School, Shield, FileText } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

// FAQ data for schema markup
const faqData = [
  {
    question: "What happened to Edmodo?",
    answer: "Edmodo, the educational social network known as the 'Facebook of Education,' shut down in September 2022 after 15 years. CSW (Chill Study Work) is now a free alternative for students seeking collaborative study environments."
  },
  {
    question: "Is CSW a free Edmodo alternative?",
    answer: "Yes, CSW is completely free to use with no limits. While Edmodo focused on K-12 classroom management, CSW offers virtual study rooms, study timers, and task management for independent learners and study groups."
  },
  {
    question: "What features does CSW offer compared to Edmodo?",
    answer: "CSW offers 24/7 study rooms for real-time study sessions, customizable Study Timer with Pomodoro technique, Task Management with Kanban boards, synchronized YouTube watching, and lo-fi music playlists—all free."
  },
  {
    question: "Can I study with others on CSW like Edmodo?",
    answer: "Absolutely! CSW's Study Stream feature lets you join or host live study sessions with students worldwide, creating the same sense of community and accountability that Edmodo provided in classrooms."
  },
  {
    question: "Does CSW have study tools like Edmodo did?",
    answer: "Yes, CSW has quizzes, flash cards, task management, and study timers. While different from Edmodo's classroom-focused tools, CSW is designed for self-directed learners who want productivity and community."
  },
  {
    question: "Why did Edmodo shut down?",
    answer: "Edmodo shut down in 2022, reportedly due to unsustainable costs of running a free platform at scale and competition from Google Classroom and Microsoft Teams. CSW continues the mission of free, accessible study tools."
  }
];

const Edmodo = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Edmodo Alternative - Free Virtual Study Rooms"
        description="Looking for an Edmodo alternative? Edmodo shut down in 2022 but CSW offers free virtual study rooms with Study Stream, Study Timer, and Task Management. Start studying smarter today."
        keywords="edmodo alternative, edmodo, educational social network, virtual study room, study together online, classroom alternative, google classroom alternative"
        canonicalUrl="/edmodo/"
      />
      
      {/* WebPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Edmodo Alternative - Free Virtual Study Rooms | CSW",
        "description": "Looking for an Edmodo alternative? Edmodo shut down in 2022 but CSW offers free virtual study rooms with Study Stream, Study Timer, and Task Management.",
        "url": "https://csw.live/edmodo/",
        "inLanguage": "en",
        "isPartOf": {
          "@type": "WebSite",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        },
        "about": {
          "@type": "Thing",
          "name": "Edmodo Alternative"
        },
        "mentions": [
          { "@type": "Thing", "name": "Edmodo" },
          { "@type": "Thing", "name": "Educational Technology" },
          { "@type": "Thing", "name": "Virtual Study Room" },
          { "@type": "Thing", "name": "Study Stream" }
        ],
        "keywords": "edmodo alternative, edmodo, educational social network, virtual study room, study together online, classroom alternative"
      })}} />
      
      {/* SoftwareApplication Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Edmodo Alternative",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web Browser",
        "description": "Free Edmodo alternative with virtual study rooms, Study Timer, and Task Management. Join unlimited study sessions with no time limits or subscription fees.",
        "url": "https://csw.live/edmodo/",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5",
          "ratingCount": "1000",
          "bestRating": "5",
          "worstRating": "1"
        },
        "featureList": [
          "Free Virtual Study Rooms",
          "24/7 Study Streams",
          "Customizable Pomodoro Timer",
          "Task Management with Kanban",
          "AI-Powered Quizzes",
          "No Subscription Required",
          "Global Study Community",
          "Lo-fi Music Integration"
        ],
        "publisher": {
          "@type": "Organization",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        }
      })}} />
      
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      })}} />
      
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in">
              <span className="text-gradient">Edmodo</span>
            </h1>
            
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Edmodo Alternative
            </h1>
            
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight animate-fade-in text-muted-foreground" style={{ animationDelay: '0.2s' }}>
              Free Virtual Study Rooms{" "}
              <span className="text-gradient">24/7</span>
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              When Edmodo shut down in 2022, millions of students lost their collaborative learning space. CSW fills that void with free, unlimited study sessions—no subscription required. Join our global community of <Link to="/study-with-me/" className="text-primary hover:underline">productive learners</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Studying Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-medium">No Limits</span>
                </div>
              </div>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See CSW in action</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Edmodo Shutdown Notice */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-primary/20">
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-7 h-7 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                    Edmodo Has <span className="text-gradient">Shut Down</span>
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Edmodo—the "Facebook of Education" that connected over 100 million teachers and students—shut down in September 2022 after 15 years. Despite being UNESCO-recommended and widely beloved, it couldn't sustain its free model. <Link to="/edmodo/history/" className="text-primary hover:underline font-medium">Read the full Edmodo story →</Link>
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    <span className="font-semibold text-foreground">CSW – Chill Study Work</span> continues the mission of free, accessible learning tools. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link>, <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tools</Link>, and <Link to="/study-with-friends/" className="text-primary hover:underline">collaborative study modes</Link>.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Study Stream Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-xs font-medium text-secondary">
                COMMUNITY LEARNING
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study <span className="text-gradient">Together</span>
              </h2>
              <p className="text-xl text-muted-foreground font-light">
                Edmodo connected classrooms. CSW connects learners worldwide.
              </p>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  While Edmodo focused on teacher-led classrooms, CSW's <Link to="/study-stream/" className="text-primary hover:underline">Study Stream</Link> empowers independent learners. Join 24/7 study rooms, find accountability partners, and experience the power of <Link to="/study-with-strangers/" className="text-primary hover:underline">studying with strangers</Link>.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  The same sense of community Edmodo fostered in schools, CSW brings to self-directed learners everywhere. Discover how <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="text-primary hover:underline">virtual co-presence boosts productivity</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Users className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Global Study Community</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Study Timer Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 order-2 lg:order-1 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <Clock className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Customizable Study Timer</p>
            </Card>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study <span className="text-gradient">Timer</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Edmodo had assignment deadlines; CSW has the Pomodoro technique. Our customizable <Link to="/study-timer/" className="text-primary hover:underline">study timer</Link> helps you structure focused work sessions with breaks—proven to boost retention and prevent burnout.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Set intervals, customize backgrounds, and get visual and audio cues to stay on track. The timer integrates with study rooms for synchronized group sessions. Learn more <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline">digital tools for academic success</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Task Management Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Task <span className="text-gradient">Management</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Edmodo helped teachers assign work; CSW helps you organize it. Our <Link to="/task-management/" className="text-primary hover:underline">task management</Link> with Kanban boards lets you create, prioritize, and track your study goals—no teacher required.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Set <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>, break down projects into tasks, and visualize your progress. Perfect for exam prep, research projects, or daily study routines. Discover how <Link to="/blog/apps-boost-organization-college/" className="text-primary hover:underline">apps boost organization in college</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-secondary/10 to-primary/10 border-secondary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Kanban Task Board</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Quizzes & Flashcards Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 order-2 lg:order-1 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <FileText className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">AI-Powered Quizzes & Flashcards</p>
            </Card>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Quizzes & <span className="text-gradient">Flash Cards</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Like Edmodo's quiz features, CSW offers <Link to="/quizzes/" className="text-primary hover:underline">AI-powered quizzes</Link> and <Link to="/flash-cards/" className="text-primary hover:underline">flash cards</Link> for effective studying. Create custom study materials or generate them with AI—no limits on how much you can learn.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Test yourself, track progress, and master any subject. Whether you're preparing for finals or learning something new, these tools help you study smarter. Learn about <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">the science behind study with me</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                <HelpCircle className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="border border-border/50 rounded-xl px-6 bg-card/30 backdrop-blur-sm"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold text-foreground">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-12 sm:p-16 bg-card/50 backdrop-blur-sm border-primary/20 text-center">
            <div className="space-y-8">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Ready to Start <span className="text-gradient">Studying?</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Join thousands of learners who found their new study home after Edmodo. Free forever, no limits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="font-semibold px-8 py-6 text-lg"
                  asChild
                >
                  <Link to="/edmodo/history/">
                    Read Edmodo's Story
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <RelatedAlternatives currentPlatform="Edmodo" />

      <Footer />
    </div>
  );
};

export default Edmodo;
